/** @jsxImportSource @emotion/react */
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { css } from "@emotion/react";
import { Divider } from "../../../components/Divider";
import { useFormContext } from "react-hook-form";
import { MutateMenusOnSalesChannelsFormValues } from "./MutateMenusOnSalesChannelsSelector";
import { PublishDestinations } from "../models/Menus";
import { getMenuHasChanges, getSomeChildHasMenuChanges } from "../menus-util";
import { colors } from "../../../components/colors";
import { Avatar } from "../../../components/Avatar";
import { StyledText } from "../../../assets/typography/typography";
import { SmallBadge } from "../../../components/SmallBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { StyledInfoBox } from "../../../components/StyledInfoBox";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  submitIsLoading: boolean;
  publishDestinations: PublishDestinations | undefined;
};

const bodyWrapper = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const shopOrSalesChannelWrapperStyle = css`
  display: flex;
  flex-direction: row;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.greyBorder};
`;

const additionalInfoStyle = css`
  margin-bottom: 16px;
`;

const innerWrapperStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  gap: 16px;
`;

const rowStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const childrenListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type MenuChangeRowProps = {
  currentMenuName: string;
  chosenMenuName: string;
  shopOrChannelName: string;
  channelName?: string;
};

const MenuChangeRow = ({
  currentMenuName,
  chosenMenuName,
  shopOrChannelName,
  channelName,
}: MenuChangeRowProps) => {
  return (
    <div css={childrenListStyle}>
      <StyledText className="bodyTextSemibold mb-0" color={colors.textPrimary}>
        {shopOrChannelName}
      </StyledText>
      {channelName && (
        <StyledText
          className="bodySubtextRegular mb-0"
          color={colors.textSecondary}
        >
          {channelName}
        </StyledText>
      )}
      <div css={rowStyle}>
        <SmallBadge>
          <StyledText
            className="bodySubtextRegular mb-0"
            color={colors.textSecondary}
          >
            {currentMenuName}
          </StyledText>
        </SmallBadge>
        <StyledText
          className="bodyTextSemibold mb-0"
          color={colors.textPrimary}
        >
          <FontAwesomeIcon icon={solid("arrow-right")} />
        </StyledText>
        <SmallBadge
          backgroundColor={colors.ctaLight}
          borderColor={colors.ctaSecondaryOne}
        >
          <StyledText
            className="bodySubtextSemibold mb-0"
            color={colors.ctaMain}
          >
            {chosenMenuName}
          </StyledText>
        </SmallBadge>
      </div>
    </div>
  );
};

export const ConfirmMenuPushModal = ({
  isOpen,
  onClose,
  onSubmit,
  publishDestinations,
  submitIsLoading,
}: Props) => {
  const methods = useFormContext<MutateMenusOnSalesChannelsFormValues>();

  const { watch } = methods;

  const formDestinations = watch("destinations");

  return (
    <ConfirmActionModal
      isOpen={isOpen}
      onClose={onClose}
      width={900}
      cancelButtonText="No, go back"
      title="Confirm your menu push"
      subtitle="Do you want to proceed with pushing menus the following menu changes?"
      confirmButton={
        <ButtonAction onClick={onSubmit} type="button">
          {submitIsLoading ? <ActionSpinner /> : "Yes, push menus"}
        </ButtonAction>
      }
    >
      <div css={bodyWrapper}>
        <StyledInfoBox
          additionalStyles={additionalInfoStyle}
          title="OBS: Changes can take 10-15 min to take effect"
          subtitle="It can take up to 15 min before you can see your changes live in sales channels."
        />
        <Divider />
        {formDestinations
          .filter((destination) => {
            const origindestination = publishDestinations?.destinations.find(
              (d) => d.id === destination.id
            );
            // Check if any child has menu changes
            const someChildHasMenuChange = getSomeChildHasMenuChanges(
              destination,
              publishDestinations?.destinations,
              formDestinations
            );
            // Check if the shop has menu changes and does not have a parent
            const menuHasNoParent = !origindestination?.parent;
            const menuHasChanges = getMenuHasChanges(
              origindestination?.currentMenus,
              destination.menus
            );

            // We want to show the shop if it has a menu change and does not have a parent or a child sales channel has menu change
            return (
              someChildHasMenuChange || (menuHasChanges && menuHasNoParent)
            );
          })
          .map((des, i) => {
            // Get chosen menu name to display in UI
            const originDestination = publishDestinations?.destinations.find(
              (destination) => des.id === destination.id
            );
            const availableMenus = publishDestinations?.availableMenus.find(
              (am) => originDestination?.availableMenusReference === am.id
            );
            const chosenMenu = availableMenus?.menus.find(
              (m) => m.id === des.menus[0]
            );
            const childrenWithMenuChange = publishDestinations?.destinations
              .filter((d) => {
                if (d.parent !== des.id) {
                  return false;
                }
                const childInForm = formDestinations.find(
                  (des) => des.id === d.id
                );
                const childHasMenuChange = getMenuHasChanges(
                  d?.currentMenus,
                  childInForm?.menus
                );

                return childHasMenuChange;
              })
              .map((d) => {
                const childInForm = formDestinations.find(
                  (des) => des.id === d.id
                );
                const availableMenusForChild = publishDestinations?.availableMenus.find(
                  (am) => d?.availableMenusReference === am.id
                );
                const chosenMenuForChild = availableMenusForChild?.menus.find(
                  (m) => m.id === childInForm?.menus[0]
                );
                return {
                  chosenMenu: chosenMenuForChild?.name,
                  currentMenu:
                    d.currentMenus &&
                    d.currentMenus.length > 0 &&
                    d.currentMenus[0].name,
                  destinationTitle: d.title,
                  destinationName: d.name,
                  platform: d.platform,
                };
              });

            return (
              <div key={i} css={shopOrSalesChannelWrapperStyle}>
                <div css={innerWrapperStyle}>
                  <div css={rowStyle}>
                    <Avatar
                      imageUrl={originDestination?.imageUrl}
                      size="medium"
                    >
                      {originDestination?.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <StyledText
                      className="bodyTextSemibold mb-0"
                      color={colors.textPrimary}
                    >
                      {originDestination?.title
                        ? originDestination.title
                        : originDestination?.name}
                    </StyledText>
                  </div>
                </div>
                <div css={innerWrapperStyle}>
                  {/* We know that no chosenMenu is there if there is children */}
                  {/* Therefor we can list both chosenMenu and childrenWithChange */}
                  {chosenMenu && (
                    <MenuChangeRow
                      chosenMenuName={chosenMenu ? chosenMenu.name : "-"}
                      shopOrChannelName="HeapsGo"
                      currentMenuName={
                        originDestination?.currentMenus &&
                        originDestination.currentMenus[0]
                          ? originDestination?.currentMenus[0].name
                          : "No current menu"
                      }
                    />
                  )}
                  {childrenWithMenuChange?.map((child, index) => (
                    <MenuChangeRow
                      key={index}
                      shopOrChannelName={
                        child.platform ? child.platform : child.destinationName
                      }
                      channelName={
                        child.platform
                          ? child.destinationName
                          : child.destinationTitle
                      }
                      chosenMenuName={
                        child.chosenMenu ? child.chosenMenu : "No menu"
                      }
                      currentMenuName={
                        child.currentMenu
                          ? child.currentMenu
                          : "No current menu"
                      }
                    />
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </ConfirmActionModal>
  );
};
